export const LIVEKIT_TRANSCRIPTION_FINAL_ATTRIBUTE = "lk.transcription_final";
export const LIVEKIT_TRANSCRIPTION_SEGMENT_ID_ATTRIBUTE = "lk.segment_id";
export const LIVEKIT_PUBLISH_ON_BEHALF_ATTRIBUTE = "lk.publish_on_behalf";

export type LiveKitTranscriptRole = "assistant" | "user";

export interface LiveKitTextStreamLike {
  text?: string;
  participantInfo?: {
    identity?: string;
  };
  streamInfo?: {
    id?: string;
    timestamp?: unknown;
    attributes?: Record<string, unknown>;
  };
}

export interface LiveKitTranscriptionIdentityContext {
  localParticipantIdentity?: string;
  agentIdentity?: string;
  workerIdentities?: string[];
}

export interface NormalizedLiveKitTranscription {
  id: string;
  text: string;
  role: LiveKitTranscriptRole;
  participantIdentity?: string;
  isFinal: boolean;
  streamTimestamp?: number;
  source: "text-stream";
}

function parseBooleanAttribute(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === "string") {
    return ["true", "1", "yes"].includes(value.toLowerCase());
  }
  return false;
}

function parseTimestamp(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "bigint") return Number(value);
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;

    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return undefined;
}

function getStreamSegmentId(stream: LiveKitTextStreamLike, index: number): string {
  const attrs = stream.streamInfo?.attributes ?? {};
  const segmentId = attrs[LIVEKIT_TRANSCRIPTION_SEGMENT_ID_ATTRIBUTE];

  if (typeof segmentId === "string" && segmentId.trim().length > 0) {
    return segmentId;
  }

  if (stream.streamInfo?.id) {
    return stream.streamInfo.id;
  }

  const participantIdentity = stream.participantInfo?.identity || "unknown";
  return `${participantIdentity}_${index}`;
}

function getRole(
  participantIdentity: string | undefined,
  identities: LiveKitTranscriptionIdentityContext
): LiveKitTranscriptRole {
  if (participantIdentity && participantIdentity === identities.localParticipantIdentity) {
    return "user";
  }

  if (participantIdentity && participantIdentity === identities.agentIdentity) {
    return "assistant";
  }

  if (participantIdentity && identities.workerIdentities?.includes(participantIdentity)) {
    return "assistant";
  }

  // LiveKit's own session-message hook treats unknown transcription senders as agent output,
  // which covers disconnected agent participants and avatar/worker variations.
  return "assistant";
}

export function normalizeLiveKitTranscriptionStreams(
  streams: LiveKitTextStreamLike[],
  identities: LiveKitTranscriptionIdentityContext
): NormalizedLiveKitTranscription[] {
  const bySegmentId = new Map<string, NormalizedLiveKitTranscription>();

  streams.forEach((stream, index) => {
    const text = typeof stream.text === "string" ? stream.text.trim() : "";
    if (!text) return;

    const attrs = stream.streamInfo?.attributes ?? {};
    const id = getStreamSegmentId(stream, index);
    const participantIdentity = stream.participantInfo?.identity;
    const role = getRole(participantIdentity, identities);
    // LiveKit's useTranscriptions cache keeps streamInfo from stream open.
    // User STT streams open with lk.transcription_final=false; aclose(final=true)
    // does not refresh those cached attributes. In-progress speech updates the
    // same segment id in place, so treating each distinct user segment as final
    // is safe for merge/display.
    const attributedFinal = parseBooleanAttribute(attrs[LIVEKIT_TRANSCRIPTION_FINAL_ATTRIBUTE]);
    const normalized: NormalizedLiveKitTranscription = {
      id,
      text,
      role,
      participantIdentity,
      isFinal: attributedFinal || role === "user",
      streamTimestamp: parseTimestamp(stream.streamInfo?.timestamp),
      source: "text-stream",
    };

    const existing = bySegmentId.get(id);
    if (existing?.isFinal && !normalized.isFinal) {
      return;
    }

    bySegmentId.set(id, normalized);
  });

  return Array.from(bySegmentId.values()).sort((a, b) => {
    const aTime = a.streamTimestamp ?? 0;
    const bTime = b.streamTimestamp ?? 0;
    return aTime - bTime;
  });
}
